import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, useTheme } from '../../theme';
import { fs, iconSize, s, v } from '../../utils/libs';
import type { PurchaseOrderStatus } from '../../redux/backoffice/suppliersSlice';
import { formatDisplayDateTime } from '../../utils/formatDisplayDateTime';

type Props = {
  status?: PurchaseOrderStatus;
  sentVia?: string[];
  sentAt?: string | null;
  variant?: 'default' | 'inline';
};

const POSendResultSection: React.FC<Props> = ({
  status,
  sentVia,
  sentAt,
  variant = 'default',
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const sentChannels = Array.isArray(sentVia) ? sentVia : [];

  const showSentInfo =
    status === 'sent' || status === 'partially_fulfilled';

  if (!showSentInfo) return null;

  const sentAtFormatted = formatDisplayDateTime(sentAt, '');

  const isInline = variant === 'inline';

  return (
    <View style={[styles.container, isInline && styles.containerInline]}>
      <Text style={styles.label}>
        Sent to supplier
        {sentAtFormatted ? ` • ${sentAtFormatted}` : ''}
      </Text>
      {sentChannels.length > 0 && (
        <View style={styles.row}>
          {sentChannels.map(channel => (
            <View key={channel} style={styles.badge}>
              <MaterialCommunityIcons
                name="check-circle"
                color={theme.colors.successDark}
                size={iconSize(14)}
              />
              <Text style={styles.badgeText}>
                {channel.charAt(0).toUpperCase() + channel.slice(1)}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      marginTop: v(8),
      paddingTop: v(8),
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.border,
    },
    containerInline: {
      marginTop: 0,
      paddingTop: 0,
      borderTopWidth: 0,
      flex: 1,
    },
    label: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      marginBottom: v(4),
    },
    row: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s(8),
    },
    badge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(4),
      backgroundColor: theme.colors.successLight,
      borderRadius: s(12),
      paddingHorizontal: s(8),
      paddingVertical: v(4),
    },
    badgeText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(14),
      color: theme.colors.successDark,
    },
  });

export default POSendResultSection;
